import dynamic from 'next/dynamic';
import type { FunctionComponent, ReactNode } from 'react';

import { getExplanationText } from '@/domains/productDiscovery/Listings/components/Filters/components/DeliveryFilter/components/DeliveryLineFilter/utils/getExplanationText';
import type { FilterExplanationProps } from '@/domains/productDiscovery/Listings/components/Filters/components/FilterExplanation/FilterExplanation';
import { Label } from '@/domains/productDiscovery/Listings/components/Filters/components/Label/Label';
import { Checkbox } from '@/domains/productDiscovery/Listings/components/Filters/components/newTamagoshiComponents/Checkbox/Checkbox';
import { useFacet } from '@/productDiscovery/Listings/facets/hooks/useFacet';

import styles from './DeliveryLineFilter.module.scss';

const DynamicFilterExplanation = dynamic<FilterExplanationProps>(() =>
  import(
    '@/domains/productDiscovery/Listings/components/Filters/components/FilterExplanation/FilterExplanation'
  ).then((mod) => mod.FilterExplanation),
);

interface DeliveryLineFilterProps {
  attributeName: string;
  className?: string;
  explanationText?: boolean;
  label: string | JSX.Element;
  onClick?(attributeName: string, newValue: boolean): void;
  manoExpress?: boolean;
  icon?: ReactNode;
}

export const DeliveryLineFilter: FunctionComponent<DeliveryLineFilterProps> = ({
  className,
  label,
  attributeName,
  onClick,
  icon,
  explanationText = false,
  manoExpress = false,
}) => {
  const { facet, refineFacetValue } = useFacet<'bool'>(attributeName);
  const value = facet?.values.find(
    (item) => item.isRefined || item.name === 'true',
  );
  const isRefined = !!value?.isRefined;

  const handleClick = () => {
    refineFacetValue(!isRefined);

    if (onClick) {
      onClick(attributeName, !isRefined);
    }
  };

  return (
    <>
      {explanationText ? (
        <div className="flex flex-row gap-xs">
          <Checkbox
            className={className}
            id={attributeName}
            isChecked={isRefined}
            value={isRefined}
            onClick={handleClick}
            isDisabled={!value?.count}
          >
            <Label label={label} disabled={!value?.count} />
            {icon}
          </Checkbox>

          <div className={styles.filterExplanationWrapper}>
            <DynamicFilterExplanation
              label="En savoir plus"
              filterExplanationText={getExplanationText(
                attributeName,
                manoExpress,
              )}
              attributeName={attributeName}
              className={styles.explanationText}
              popoverClassName={styles.popover}
            />
          </div>
        </div>
      ) : (
        <Checkbox
          className={className}
          id={attributeName}
          isChecked={isRefined}
          value={isRefined}
          onClick={handleClick}
          isDisabled={!value?.count}
        >
          <Label label={label} disabled={!value?.count} />
        </Checkbox>
      )}
    </>
  );
};

DeliveryLineFilter.displayName = 'DeliveryLineFilter';
